import {
  isValidRfc3339,
  parseAppointmentTimestamp,
  validateWireSemantics,
} from './browserContractSemantics';
import { appointmentContractV2 } from './contractCodec';

import type {
  ActiveSeriesSummary,
  AdminActiveSeriesSummary,
  AppointmentCancellationInitiator,
  AppointmentKind,
  AppointmentScope,
  LifecycleStatus,
  Meeting,
  OneOffBookingPattern,
  PlatformAdminActionCapabilities,
  PlatformAdminAppointment,
  PlatformAdminAppointmentPage,
  PlatformAdminAvailabilitySlot,
  PlatformAdminCancellation,
  PlatformAdminCancellationEvidence,
  PlatformAdminCancellationEvidencePage,
  PlatformAdminMutationResult,
  PlatformAdminOperationState,
  PlatformAdminOperationStatus,
  PlatformAdminPerson,
  PlatformAdminRescheduleOperationPage,
  PlatformAdminSchedulingContext,
  Rejection,
  RescheduleTarget,
  Schedule,
  SchedulingEligibility,
  SchedulingEligibilityReason,
  SeatType,
} from './types';

interface Decoder<T> {
  decode(value: unknown): T;
}

type JsonObject = Record<string, unknown>;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const CANONICAL_RAILS_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;

const fail = (message: string): never => {
  throw new Error(`Invalid Platform admin Appointment response: ${message}`);
};

const exactObject = (
  value: unknown,
  fields: readonly string[],
  label: string,
): JsonObject => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return fail(`${label} must be an object`);
  }
  const object = value as JsonObject;
  const actual = Object.keys(object).sort();
  const expected = [...fields].sort();
  if (
    actual.length !== expected.length ||
    actual.some((field, index) => field !== expected[index])
  ) {
    return fail(`${label} has unknown or missing fields`);
  }
  return object;
};

const stringValue = (value: unknown, label: string): string => {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return fail(`${label} must be a non-empty string`);
  }
  return value;
};

const nullableString = (value: unknown, label: string): string | null =>
  value === null ? null : stringValue(value, label);

const timestamp = (value: unknown, label: string): string => {
  const decoded = stringValue(value, label);
  if (!isValidRfc3339(decoded)) {
    return fail(`${label} must be a timestamp`);
  }
  return decoded;
};

const nullableTimestamp = (value: unknown, label: string): string | null =>
  value === null ? null : timestamp(value, label);

const uuid = (value: unknown, label: string): string => {
  const decoded = stringValue(value, label);
  if (!UUID_PATTERN.test(decoded)) return fail(`${label} must be a UUID`);
  return decoded;
};

const localRailsUuid = (value: unknown, label: string): string => {
  const decoded = stringValue(value, label);
  if (!CANONICAL_RAILS_UUID_PATTERN.test(decoded)) {
    return fail(`${label} must be a canonical Rails UUID`);
  }
  return decoded;
};

const integer = (value: unknown, label: string, minimum: number): number => {
  if (!Number.isInteger(value) || (value as number) < minimum) {
    return fail(
      `${label} must be an integer greater than or equal to ${minimum}`,
    );
  }
  return value as number;
};

const booleanValue = (value: unknown, label: string): boolean => {
  if (typeof value !== 'boolean') return fail(`${label} must be a boolean`);
  return value;
};

const enumValue = <T extends string>(
  value: unknown,
  values: readonly T[],
  label: string,
): T => {
  if (typeof value !== 'string' || !values.includes(value as T)) {
    return fail(`${label} has an unsupported value`);
  }
  return value as T;
};

const arrayValue = <T>(
  value: unknown,
  decode: (item: unknown, index: number) => T,
  label: string,
): T[] => {
  if (!Array.isArray(value)) return fail(`${label} must be an array`);
  return value.map(decode);
};

const appointmentKind = (value: unknown): AppointmentKind =>
  enumValue(value, ['chemistry', 'coaching'], 'kind');

const seatType = (value: unknown): SeatType | null =>
  value === null
    ? null
    : enumValue<SeatType>(value, ['leadership', 'executive'], 'seat_type');

const mutationStatus = (value: unknown): PlatformAdminOperationState =>
  enumValue(
    value,
    ['pending', 'confirmed', 'needs_reconciliation', 'rejected'],
    'status',
  );

const operationState = (value: unknown): PlatformAdminOperationState =>
  enumValue(
    value,
    ['pending', 'confirmed', 'needs_reconciliation', 'rejected'],
    'status',
  );

const appointmentScope = (value: unknown): AppointmentScope =>
  enumValue(value, ['appointment', 'this_and_following'], 'scope');

const appointmentScopes = (value: unknown): AppointmentScope[] => {
  const scopes = arrayValue(value, appointmentScope, 'scopes');
  if (
    scopes.some(
      (scope, index) =>
        scopes.indexOf(scope) !== index ||
        (index > 0 &&
          scopes[index - 1] === 'this_and_following' &&
          scope === 'appointment'),
    )
  ) {
    return fail('scopes must be unique and canonically ordered');
  }
  return scopes;
};

const schedule = (value: unknown): Schedule => {
  const object = exactObject(
    value,
    ['end_at', 'start_at', 'timezone'],
    'schedule',
  );
  return {
    endAt: stringValue(object.end_at, 'end_at'),
    startAt: stringValue(object.start_at, 'start_at'),
    timezone: stringValue(object.timezone, 'timezone'),
  };
};

const meeting = (value: unknown): Meeting => {
  const object = exactObject(
    value,
    ['google_meet_code', 'join_url', 'location'],
    'meeting',
  );
  return {
    googleMeetCode: nullableString(object.google_meet_code, 'google_meet_code'),
    joinUrl: nullableString(object.join_url, 'join_url'),
    location: nullableString(object.location, 'location'),
  };
};

const person = (value: unknown): PlatformAdminPerson => {
  const object = exactObject(value, ['display_name', 'id'], 'person');
  return {
    displayName: stringValue(object.display_name, 'display_name'),
    id: localRailsUuid(object.id, 'local person id'),
  };
};

const rejection = (value: unknown): Rejection | null => {
  if (value === null) return null;
  const object = exactObject(value, ['code', 'message'], 'rejection');
  return {
    code: stringValue(object.code, 'rejection code'),
    message: stringValue(object.message, 'rejection message'),
  };
};

const cancellation = (value: unknown): PlatformAdminCancellation | null => {
  if (value === null) return null;
  const object = exactObject(
    value,
    [
      'initiator',
      'near_term_chemistry_confirmed',
      'ops_note',
      'reason',
      'requested_at',
      'scope',
    ],
    'cancellation',
  );
  return {
    initiator:
      object.initiator === null
        ? null
        : enumValue<AppointmentCancellationInitiator>(
            object.initiator,
            ['coach', 'member', 'admin'],
            'initiator',
          ),
    nearTermChemistryConfirmed: booleanValue(
      object.near_term_chemistry_confirmed,
      'near_term_chemistry_confirmed',
    ),
    opsNote: nullableString(object.ops_note, 'ops_note'),
    reason: nullableString(object.reason, 'reason'),
    requestedAt: stringValue(object.requested_at, 'requested_at'),
    scope: appointmentScope(object.scope),
  };
};

const actionCapabilities = (
  value: unknown,
): PlatformAdminActionCapabilities => {
  const object = exactObject(
    value,
    ['cancel', 'join', 'reschedule'],
    'action_capabilities',
  );
  const join = exactObject(object.join, ['allowed'], 'join capability');
  const reschedule = exactObject(
    object.reschedule,
    ['allowed', 'scopes'],
    'reschedule capability',
  );
  const cancel = exactObject(
    object.cancel,
    [
      'allowed',
      'initiator_required',
      'near_term_confirmation_required',
      'ops_note_required',
      'reason_required',
      'scopes',
    ],
    'cancel capability',
  );
  return {
    cancel: {
      allowed: booleanValue(cancel.allowed, 'cancel allowed'),
      initiatorRequired: booleanValue(
        cancel.initiator_required,
        'initiator_required',
      ),
      nearTermConfirmationRequired: booleanValue(
        cancel.near_term_confirmation_required,
        'near_term_confirmation_required',
      ),
      opsNoteRequired: booleanValue(
        cancel.ops_note_required,
        'ops_note_required',
      ),
      reasonRequired: booleanValue(cancel.reason_required, 'reason_required'),
      scopes: appointmentScopes(cancel.scopes),
    },
    join: { allowed: booleanValue(join.allowed, 'join allowed') },
    reschedule: {
      allowed: booleanValue(reschedule.allowed, 'reschedule allowed'),
      scopes: appointmentScopes(reschedule.scopes),
    },
  };
};

const decodeAppointment = (value: unknown): PlatformAdminAppointment => {
  validateWireSemantics(value);
  const object = exactObject(
    value,
    [
      'action_capabilities',
      'baseline_schedule',
      'booking_status',
      'calendar_event_id',
      'cancellation',
      'coach',
      'contract_version',
      'current_schedule',
      'id',
      'kind',
      'lifecycle_status',
      'meeting',
      'member',
      'seat_type',
      'series_id',
      'series_mutation_version',
      'version',
    ],
    'Appointment',
  );
  if (object.contract_version !== 'platform-admin-appointment.v1') {
    return fail('Appointment contract_version is invalid');
  }
  const kind = appointmentKind(object.kind);
  const decodedSeatType = seatType(object.seat_type);
  if (
    (kind === 'chemistry' && decodedSeatType !== null) ||
    (kind === 'coaching' && decodedSeatType === null)
  ) {
    return fail('Appointment kind and seat_type are inconsistent');
  }
  const seriesId =
    object.series_id === null ? null : uuid(object.series_id, 'series_id');
  const seriesMutationVersion = integer(
    object.series_mutation_version,
    'series_mutation_version',
    0,
  );
  if (
    (seriesId === null && seriesMutationVersion !== 0) ||
    (seriesId !== null && seriesMutationVersion === 0)
  ) {
    return fail('series_id and series_mutation_version are inconsistent');
  }
  const coach = person(object.coach);
  const member = person(object.member);
  if (coach.id === member.id) {
    return fail('local coach and member identities must be distinct');
  }
  return {
    actionCapabilities: actionCapabilities(object.action_capabilities),
    baselineSchedule: schedule(object.baseline_schedule),
    bookingStatus: enumValue(
      object.booking_status,
      ['pending', 'confirmed', 'rejected'],
      'booking_status',
    ),
    calendarEventId: nullableString(
      object.calendar_event_id,
      'calendar_event_id',
    ),
    cancellation: cancellation(object.cancellation),
    coach,
    contractVersion: 'platform-admin-appointment.v1',
    currentSchedule: schedule(object.current_schedule),
    id: uuid(object.id, 'Appointment id'),
    kind,
    lifecycleStatus: enumValue<LifecycleStatus>(
      object.lifecycle_status,
      ['scheduled', 'cancellation_pending', 'cancelled', 'reschedule_pending'],
      'lifecycle_status',
    ),
    meeting: meeting(object.meeting),
    member,
    seatType: decodedSeatType,
    seriesId,
    seriesMutationVersion,
    version: integer(object.version, 'version', 1),
  };
};

const decodeAppointmentPage = (
  value: unknown,
): PlatformAdminAppointmentPage => {
  const object = exactObject(
    value,
    ['appointments', 'contract_version'],
    'Appointment page',
  );
  if (object.contract_version !== 'platform-admin-appointment-page.v1') {
    return fail('Appointment page contract_version is invalid');
  }
  const appointments = arrayValue(
    object.appointments,
    decodeAppointment,
    'appointments',
  );
  if (appointments.length > 100)
    return fail('Appointment page exceeds 100 rows');
  return {
    appointments,
    contractVersion: 'platform-admin-appointment-page.v1',
  };
};

const eligibility = (value: unknown): SchedulingEligibility => {
  const object = exactObject(value, ['eligible', 'reasons'], 'eligibility');
  const reasons = arrayValue(
    object.reasons,
    (reason) =>
      enumValue<SchedulingEligibilityReason>(
        reason,
        [
          'active_series_exists',
          'calendar_not_ready',
          'coach_unavailable',
          'member_ineligible',
          'appointment_not_reschedulable',
          'identity_mismatch',
          'member_engagement_end_missing',
          'member_engagement_ended',
          'member_engagement_unavailable',
        ],
        'eligibility reason',
      ),
    'eligibility reasons',
  );
  const eligible = booleanValue(object.eligible, 'eligible');
  if (eligible !== (reasons.length === 0)) {
    return fail('eligibility and reasons are inconsistent');
  }
  return { eligible, reasons };
};

const calendarConflict = (
  value: unknown,
  minimumOccurrenceCount: number,
): PlatformAdminAvailabilitySlot['calendarConflict'] => {
  if (value === null) return null;
  const object = exactObject(
    value,
    ['calendars', 'checked_occurrence_count', 'conflicting_occurrence_count'],
    'calendar conflict',
  );
  const checkedOccurrenceCount = integer(
    object.checked_occurrence_count,
    'checked occurrence count',
    minimumOccurrenceCount,
  );
  const conflictingOccurrenceCount = integer(
    object.conflicting_occurrence_count,
    'conflicting occurrence count',
    minimumOccurrenceCount,
  );
  if (
    checkedOccurrenceCount > 6 ||
    conflictingOccurrenceCount > checkedOccurrenceCount
  ) {
    return fail('calendar conflict occurrence counts are inconsistent');
  }
  const calendars = arrayValue(
    object.calendars,
    (calendarValue) => {
      const calendar = exactObject(
        calendarValue,
        ['calendar_summary', 'provider'],
        'conflict calendar',
      );
      return {
        calendarSummary: stringValue(
          calendar.calendar_summary,
          'calendar summary',
        ),
        provider: stringValue(calendar.provider, 'calendar provider'),
      };
    },
    'conflict calendars',
  );
  if (
    calendars.length === 0 ||
    new Set(calendars.map((calendar) => JSON.stringify(calendar))).size !==
      calendars.length
  ) {
    return fail('conflict calendars must be non-empty and unique');
  }
  return {
    calendars,
    checkedOccurrenceCount,
    conflictingOccurrenceCount,
  };
};

const availability = (
  value: unknown,
  minimumOccurrenceCount: number,
): PlatformAdminSchedulingContext['availability'] => {
  const object = exactObject(value, ['days'], 'availability');
  return {
    days: arrayValue(
      object.days,
      (dayValue) => {
        const day = exactObject(
          dayValue,
          ['appointment_count', 'date', 'enabled', 'slots'],
          'availability day',
        );
        return {
          appointmentCount:
            day.appointment_count === null
              ? null
              : integer(day.appointment_count, 'day appointment_count', 0),
          date: stringValue(day.date, 'date'),
          enabled: booleanValue(day.enabled, 'enabled'),
          slots: arrayValue(
            day.slots,
            (slotValue) => {
              const slot = exactObject(
                slotValue,
                ['calendar_conflict', 'end_at', 'label', 'start_at'],
                'availability slot',
              );
              return {
                calendarConflict: calendarConflict(
                  slot.calendar_conflict,
                  minimumOccurrenceCount,
                ),
                endAt: stringValue(slot.end_at, 'slot end_at'),
                label: stringValue(slot.label, 'slot label'),
                startAt: stringValue(slot.start_at, 'slot start_at'),
              };
            },
            'availability slots',
          ),
        };
      },
      'availability days',
    ),
  };
};

const activeSeries = (value: unknown): AdminActiveSeriesSummary | null => {
  if (value === null) return null;
  const object = exactObject(
    value,
    [
      'affected_end_at',
      'cadence_weeks',
      'id',
      'next_appointment_id',
      'next_schedule',
      'remaining_appointment_count',
    ],
    'active_series',
  );
  const base: ActiveSeriesSummary = {
    cadenceWeeks: integer(object.cadence_weeks, 'cadence_weeks', 1),
    id: uuid(object.id, 'active Series id'),
    nextAppointmentId: uuid(object.next_appointment_id, 'next Appointment id'),
    nextSchedule: schedule(object.next_schedule),
    remainingAppointmentCount: integer(
      object.remaining_appointment_count,
      'remaining_appointment_count',
      1,
    ),
  };
  return {
    ...base,
    affectedEndAt: stringValue(object.affected_end_at, 'affected_end_at'),
  };
};

const rescheduleTarget = (value: unknown): RescheduleTarget | null => {
  if (value === null) return null;
  const object = exactObject(
    value,
    [
      'allowed_scopes',
      'current_schedule',
      'id',
      'series_id',
      'series_mutation_version',
      'version',
    ],
    'reschedule_target',
  );
  return {
    allowedScopes: appointmentScopes(object.allowed_scopes),
    currentSchedule: schedule(object.current_schedule),
    id: uuid(object.id, 'reschedule target id'),
    seriesId:
      object.series_id === null
        ? null
        : uuid(object.series_id, 'reschedule target series_id'),
    seriesMutationVersion: integer(
      object.series_mutation_version,
      'reschedule target series_mutation_version',
      0,
    ),
    version: integer(object.version, 'reschedule target version', 1),
  };
};

const warnings = (value: unknown): 'near_term_coaching_reschedule'[] =>
  arrayValue(
    value,
    (warning) =>
      enumValue(warning, ['near_term_coaching_reschedule'], 'warning'),
    'warnings',
  );

const validateSchedulingContextSemantics = (
  context: PlatformAdminSchedulingContext,
): void => {
  if (context.coach.id === context.member.id) {
    fail('local coach and member identities must be distinct');
  }
  context.availability.days.forEach((day, dayIndex, days) => {
    if (dayIndex > 0 && day.date <= days[dayIndex - 1]!.date) {
      fail('availability days must be uniquely ordered by date');
    }
    if (!day.enabled && day.slots.length > 0) {
      fail('disabled availability day must have no slots');
    }
    if (context.kind === 'chemistry' && day.appointmentCount !== null) {
      fail('chemistry availability counts must be null');
    }
    if (
      context.appointmentCount !== null &&
      day.enabled &&
      day.appointmentCount !== context.appointmentCount
    ) {
      fail('enabled day count must match appointment_count');
    }
    day.slots.forEach((slot, slotIndex, slots) => {
      if (
        slotIndex > 0 &&
        parseAppointmentTimestamp(slot.startAt) <=
          parseAppointmentTimestamp(slots[slotIndex - 1]!.startAt)
      ) {
        fail('availability slots must be uniquely ordered by start');
      }
    });
  });
  if (
    context.activeSeries !== null &&
    parseAppointmentTimestamp(context.activeSeries.affectedEndAt) <
      parseAppointmentTimestamp(context.activeSeries.nextSchedule.endAt)
  ) {
    fail('active Series affected end must bound its next Appointment');
  }
  const target = context.rescheduleTarget;
  if (target === null) return;
  if (
    (target.seriesId === null && target.seriesMutationVersion !== 0) ||
    (target.seriesId !== null && target.seriesMutationVersion === 0)
  ) {
    fail('reschedule target Series fence is inconsistent');
  }
  if (
    context.kind === 'chemistry' &&
    (target.seriesId !== null ||
      target.allowedScopes.some((scope) => scope !== 'appointment'))
  ) {
    fail('chemistry reschedule target must be standalone');
  }
  if (
    context.activeSeries !== null &&
    target.seriesId !== context.activeSeries.id
  ) {
    fail('active Series must match the reschedule target');
  }
};

const bookingPattern = (value: unknown): OneOffBookingPattern => {
  const object = exactObject(value, ['type'], 'booking_pattern');
  if (object.type !== 'one_off') return fail('booking_pattern is invalid');
  return { type: 'one_off' };
};

const decodeSchedulingContext = (
  value: unknown,
): PlatformAdminSchedulingContext => {
  validateWireSemantics(value);
  const hasBookingPattern =
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'booking_pattern' in value;
  const object = exactObject(
    value,
    [
      'active_series',
      'appointment_count',
      'availability',
      ...(hasBookingPattern ? ['booking_pattern'] : []),
      'cadence_weeks',
      'coach',
      'contract_version',
      'duration_minutes',
      'eligibility',
      'kind',
      'member',
      'reschedule_target',
      'seat_type',
      'timezone',
      'visible_weeks',
      'warnings',
    ],
    'scheduling context',
  );
  if (object.contract_version !== 'platform-admin-scheduling-context.v1') {
    return fail('scheduling context contract_version is invalid');
  }
  const memberObject = exactObject(
    object.member,
    ['display_name', 'email', 'id'],
    'scheduling member',
  );
  const kind = appointmentKind(object.kind);
  const decodedSeatType = seatType(object.seat_type);
  const appointmentCount =
    object.appointment_count === null
      ? null
      : integer(object.appointment_count, 'appointment_count', 1);
  const cadenceWeeks =
    object.cadence_weeks === null
      ? null
      : integer(object.cadence_weeks, 'cadence_weeks', 1);
  const decodedActiveSeries = activeSeries(object.active_series);
  const decodedBookingPattern =
    object.booking_pattern === undefined
      ? null
      : bookingPattern(object.booking_pattern);
  const oneOff = decodedBookingPattern !== null;
  const decodedRescheduleTarget = rescheduleTarget(object.reschedule_target);
  // MEN-5306: mirrors the backend's standaloneReschedule rule. Rescheduling a
  // target without Series membership has no cadence; recurring contexts and
  // bookings still require one.
  const standaloneReschedule =
    !oneOff &&
    decodedRescheduleTarget !== null &&
    decodedRescheduleTarget.seriesId === null &&
    appointmentCount === null;
  if (
    (kind === 'chemistry' &&
      (decodedSeatType !== null ||
        appointmentCount !== null ||
        cadenceWeeks !== null ||
        decodedActiveSeries !== null ||
        oneOff)) ||
    (kind === 'coaching' &&
      (decodedSeatType === null ||
        (oneOff
          ? appointmentCount !== null || cadenceWeeks !== null
          : cadenceWeeks === null && !standaloneReschedule)))
  ) {
    return fail('scheduling kind fields are inconsistent');
  }
  const context: PlatformAdminSchedulingContext = {
    activeSeries: decodedActiveSeries,
    appointmentCount,
    availability: availability(object.availability, oneOff ? 1 : 2),
    bookingPattern: decodedBookingPattern,
    cadenceWeeks,
    coach: person(object.coach),
    contractVersion: 'platform-admin-scheduling-context.v1',
    durationMinutes: integer(object.duration_minutes, 'duration_minutes', 1),
    eligibility: eligibility(object.eligibility),
    kind,
    member: {
      displayName: stringValue(
        memberObject.display_name,
        'member display_name',
      ),
      email: stringValue(memberObject.email, 'member email'),
      id: localRailsUuid(memberObject.id, 'member local id'),
    },
    rescheduleTarget: decodedRescheduleTarget,
    seatType: decodedSeatType,
    timezone: stringValue(object.timezone, 'timezone'),
    visibleWeeks: integer(object.visible_weeks, 'visible_weeks', 1),
    warnings: warnings(object.warnings),
  };
  validateSchedulingContextSemantics(context);
  return context;
};

const validateResultState = (
  status: PlatformAdminOperationState,
  appointments: PlatformAdminAppointment[],
  decodedRejection: Rejection | null,
  retryAfterSeconds: number | null,
  pendingAppointmentsAllowed: boolean,
  needsReconciliationRejectionRequired: boolean,
): void => {
  let validAppointments = appointments.length === 0;
  if (status === 'confirmed') validAppointments = appointments.length > 0;
  if (status === 'pending') {
    validAppointments = pendingAppointmentsAllowed
      ? appointments.length > 0
      : appointments.length === 0;
  }
  const validRejection =
    status === 'rejected' ||
    (needsReconciliationRejectionRequired && status === 'needs_reconciliation')
      ? decodedRejection !== null
      : decodedRejection === null;
  const validRetry = status === 'pending' || retryAfterSeconds === null;
  if (!validAppointments || !validRejection || !validRetry) {
    fail('result status fields are inconsistent');
  }
};

const retryAfterSeconds = (value: unknown): number | null =>
  value === null ? null : integer(value, 'retry_after_seconds', 0);

type CancellationEvidenceActor = PlatformAdminCancellationEvidence['actor'];

const cancellationEvidenceActorAssistant = (
  value: unknown,
): NonNullable<CancellationEvidenceActor['assistant']> => {
  const object = exactObject(
    value,
    ['assistantId', 'displayName'],
    'cancellation evidence actor assistant',
  );
  return {
    assistantId: uuid(object.assistantId, 'actor assistantId'),
    displayName: stringValue(object.displayName, 'actor assistant displayName'),
  };
};

// Moments adds assistant only when a scheduling assistant acted (MEN-5598).
const cancellationEvidenceActor = (
  value: unknown,
): CancellationEvidenceActor => {
  const hasAssistant =
    value !== null && typeof value === 'object' && 'assistant' in value;
  const fields = ['kind', 'personId', 'role'];
  const object = exactObject(
    value,
    hasAssistant ? [...fields, 'assistant'] : fields,
    'cancellation evidence actor',
  );
  if (object.kind !== 'person') {
    return fail('cancellation evidence actor kind is invalid');
  }
  return {
    ...(hasAssistant && {
      assistant: cancellationEvidenceActorAssistant(object.assistant),
    }),
    kind: 'person',
    personId:
      object.personId === null
        ? null
        : localRailsUuid(object.personId, 'actor personId'),
    role:
      object.role === null
        ? null
        : enumValue<'coach' | 'member' | 'admin'>(
            object.role,
            ['coach', 'member', 'admin'],
            'cancellation evidence actor role',
          ),
  };
};

const cancellationEvidence = (
  value: unknown,
): PlatformAdminCancellationEvidence => {
  const object = exactObject(
    value,
    [
      'actor',
      'affectedCount',
      'affectedEndAt',
      'affectedStartAt',
      'appointmentIds',
      'correlationStatus',
      'evidenceAvailable',
      'initiator',
      'historyId',
      'nearTermAcknowledged',
      'operationId',
      'opsNote',
      'proximity',
      'reconciliationReason',
      'rejection',
      'requestedAt',
      'scope',
      'source',
      'status',
      'terminalResult',
    ],
    'cancellation evidence',
  );
  if (object.source !== 'platform') {
    return fail('cancellation evidence source is invalid');
  }
  const evidence: PlatformAdminCancellationEvidence = {
    actor: cancellationEvidenceActor(object.actor),
    affectedCount:
      object.affectedCount === null
        ? null
        : integer(object.affectedCount, 'affectedCount', 1),
    affectedEndAt: nullableTimestamp(object.affectedEndAt, 'affectedEndAt'),
    affectedStartAt: nullableTimestamp(
      object.affectedStartAt,
      'affectedStartAt',
    ),
    appointmentIds: arrayValue(
      object.appointmentIds,
      (id) => uuid(id, 'appointmentId'),
      'appointmentIds',
    ),
    correlationStatus: enumValue(
      object.correlationStatus,
      ['operation', 'correlated_legacy', 'uncorrelated_legacy'],
      'correlationStatus',
    ),
    evidenceAvailable: booleanValue(
      object.evidenceAvailable,
      'evidenceAvailable',
    ),
    initiator:
      object.initiator === null
        ? null
        : enumValue<AppointmentCancellationInitiator>(
            object.initiator,
            ['coach', 'member', 'admin'],
            'initiator',
          ),
    historyId:
      object.historyId === null ? null : uuid(object.historyId, 'historyId'),
    nearTermAcknowledged: booleanValue(
      object.nearTermAcknowledged,
      'nearTermAcknowledged',
    ),
    operationId:
      object.operationId === null
        ? null
        : uuid(object.operationId, 'operationId'),
    opsNote: nullableString(object.opsNote, 'opsNote'),
    proximity: enumValue(
      object.proximity,
      ['within_48_hours', 'outside_48_hours', 'unavailable'],
      'proximity',
    ),
    reconciliationReason:
      object.reconciliationReason === null
        ? null
        : enumValue<
            | 'provider_outcome_ambiguous'
            | 'partial_provider_effect'
            | 'persistence_recovery_required'
            | 'recovery_exhausted'
          >(
            object.reconciliationReason,
            [
              'provider_outcome_ambiguous',
              'partial_provider_effect',
              'persistence_recovery_required',
              'recovery_exhausted',
            ],
            'reconciliationReason',
          ),
    rejection: rejection(object.rejection),
    requestedAt: timestamp(object.requestedAt, 'requestedAt'),
    scope: appointmentScope(object.scope),
    source: 'platform',
    status: operationState(object.status),
    terminalResult:
      object.terminalResult === null
        ? null
        : enumValue<'confirmed' | 'rejected'>(
            object.terminalResult,
            ['confirmed', 'rejected'],
            'terminalResult',
          ),
  };
  if (evidence.operationId === null && evidence.historyId === null) {
    return fail('cancellation evidence requires operationId or historyId');
  }
  if (
    evidence.actor.personId === null &&
    (evidence.actor.role !== null || evidence.correlationStatus === 'operation')
  ) {
    return fail(
      'cancellation evidence actor personId is required for authenticated actors',
    );
  }
  if (
    evidence.appointmentIds.length === 0 ||
    new Set(evidence.appointmentIds).size !== evidence.appointmentIds.length
  ) {
    return fail(
      'cancellation evidence appointmentIds must be non-empty and unique',
    );
  }
  const validState =
    (evidence.status === 'pending' &&
      evidence.terminalResult === null &&
      evidence.rejection === null &&
      evidence.reconciliationReason === null) ||
    (evidence.status === 'confirmed' &&
      evidence.terminalResult === 'confirmed' &&
      evidence.rejection === null &&
      evidence.reconciliationReason === null) ||
    (evidence.status === 'rejected' &&
      evidence.terminalResult === 'rejected' &&
      evidence.rejection !== null &&
      evidence.reconciliationReason === null) ||
    (evidence.status === 'needs_reconciliation' &&
      evidence.terminalResult === null &&
      evidence.rejection === null &&
      evidence.reconciliationReason !== null);
  if (!validState) {
    return fail('cancellation evidence status fields are inconsistent');
  }
  return evidence;
};

const decodeCancellationEvidencePage = (
  value: unknown,
): PlatformAdminCancellationEvidencePage => {
  const object = exactObject(
    value,
    ['contractVersion', 'evidence', 'nextCursor', 'observedAt'],
    'cancellation evidence page',
  );
  if (
    object.contractVersion !== 'platform-admin-cancellation-evidence-page.v1'
  ) {
    return fail('cancellation evidence page contractVersion is invalid');
  }
  return {
    contractVersion: 'platform-admin-cancellation-evidence-page.v1',
    evidence: arrayValue(
      object.evidence,
      cancellationEvidence,
      'cancellation evidence',
    ),
    nextCursor: nullableString(object.nextCursor, 'nextCursor'),
    observedAt: timestamp(object.observedAt, 'observedAt'),
  };
};

const decodeMutationResult = (value: unknown): PlatformAdminMutationResult => {
  const object = exactObject(
    value,
    [
      'appointments',
      'contract_version',
      'operation_id',
      'rejection',
      'retry_after_seconds',
      'status',
      'warnings',
    ],
    'mutation result',
  );
  if (object.contract_version !== 'platform-admin-mutation-result.v1') {
    return fail('mutation result contract_version is invalid');
  }
  const status = mutationStatus(object.status);
  const appointments = arrayValue(
    object.appointments,
    decodeAppointment,
    'mutation appointments',
  );
  const decodedRejection = rejection(object.rejection);
  const decodedRetry = retryAfterSeconds(object.retry_after_seconds);
  validateResultState(
    status,
    appointments,
    decodedRejection,
    decodedRetry,
    true,
    false,
  );
  return {
    appointments,
    contractVersion: 'platform-admin-mutation-result.v1',
    operationId: uuid(object.operation_id, 'operation_id'),
    rejection: decodedRejection,
    retryAfterSeconds: decodedRetry,
    status,
    warnings: warnings(object.warnings),
  };
};

const decodeOperationStatus = (
  value: unknown,
): PlatformAdminOperationStatus => {
  validateWireSemantics(value);
  const object = exactObject(
    value,
    [
      'appointments',
      'command',
      'contract_version',
      'id',
      'rejection',
      'retry_after_seconds',
      'status',
      'submitted_at',
      'updated_at',
    ],
    'operation status',
  );
  if (object.contract_version !== 'platform-admin-operation.v1') {
    return fail('operation status contract_version is invalid');
  }
  const status = operationState(object.status);
  const appointments = arrayValue(
    object.appointments,
    decodeAppointment,
    'operation appointments',
  );
  const decodedRejection = rejection(object.rejection);
  const decodedRetry = retryAfterSeconds(object.retry_after_seconds);
  validateResultState(
    status,
    appointments,
    decodedRejection,
    decodedRetry,
    false,
    true,
  );
  return {
    appointments,
    command: enumValue(
      object.command,
      ['book', 'reschedule', 'cancel'],
      'operation command',
    ),
    contractVersion: 'platform-admin-operation.v1',
    id: uuid(object.id, 'operation id'),
    rejection: decodedRejection,
    retryAfterSeconds: decodedRetry,
    status,
    submittedAt: stringValue(object.submitted_at, 'submitted_at'),
    updatedAt: stringValue(object.updated_at, 'updated_at'),
  };
};

const PROJECTION_COACH_ID = '11111111-1111-4111-8111-111111111111';
const PROJECTION_MEMBER_ID = '22222222-2222-4222-8222-222222222222';

const decodeRescheduleOperationPage = (
  value: unknown,
): PlatformAdminRescheduleOperationPage => {
  validateWireSemantics(value);
  const page = exactObject(
    value,
    ['contract_version', 'next_cursor', 'observed_at', 'operations'],
    'reschedule operation page',
  );
  if (page.contract_version !== 'platform-admin-reschedule-operation-page.v2') {
    return fail('reschedule operation page contract_version is invalid');
  }
  const operations = arrayValue(
    page.operations,
    (operation) => {
      const projected = exactObject(
        operation,
        [
          'actor',
          'affected_count',
          'affected_end_at',
          'affected_start_at',
          'busy_conflicts_acknowledged',
          'conflicts',
          'evidence_available',
          'near_term_acknowledged',
          'operation_id',
          'original_schedules',
          'phase',
          'proposed_schedules',
          'reason',
          'requested_at',
          'scope',
          'source',
          'status',
          'terminal_result',
          'timing',
        ],
        'reschedule operation',
      );
      const actor =
        projected.actor === null
          ? null
          : exactObject(
              projected.actor,
              ['role'],
              'reschedule operation actor',
            );
      if (actor !== null && actor.role !== 'coach' && actor.role !== 'admin') {
        return fail('reschedule operation actor role is invalid');
      }
      return {
        ...projected,
        actor:
          actor === null
            ? null
            : { person_id: PROJECTION_COACH_ID, role: actor.role },
        coach_person_id: PROJECTION_COACH_ID,
        member_person_id: PROJECTION_MEMBER_ID,
      };
    },
    'reschedule operations',
  );
  const decoded = appointmentContractV2.adminRescheduleOperationPage.decode({
    ...page,
    contract_version: 'appointment.reschedule-operation-page.admin.v2',
    operations,
  });
  return {
    contractVersion: 'platform-admin-reschedule-operation-page.v2',
    nextCursor: decoded.nextCursor,
    observedAt: decoded.observedAt,
    operations: decoded.operations.map(
      ({ coachPersonId: _coach, memberPersonId: _member, ...operation }) => ({
        ...operation,
        actor: operation.actor ? { role: operation.actor.role } : null,
      }),
    ),
  };
};

const decoder = <T>(decode: (value: unknown) => T): Decoder<T> => ({ decode });

export const platformAdminContract = {
  appointment: decoder(decodeAppointment),
  appointmentPage: decoder(decodeAppointmentPage),
  cancellationEvidencePage: decoder(decodeCancellationEvidencePage),
  mutationResult: decoder(decodeMutationResult),
  operationStatus: decoder(decodeOperationStatus),
  rescheduleOperationPage: decoder(decodeRescheduleOperationPage),
  schedulingContext: decoder(decodeSchedulingContext),
};
